import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import Axios from "axios";
import type {
  StagingUserCredentials,
  StagingUsersConfig,
  UserSwitcherContextType,
  UserSwitcherState,
} from "./types";
import type { Permission, User } from "../../types/types";
import { useStoreState } from "../../util/util";
import { useAuthContext } from "../Auth";
import { endpoints } from "../../endpoints";
import stagingUsersConfig from "./staging-users-config.json";

const STAGING_HOST = "stagingv3.agilischemicals.com";
const STORAGE_KEY = "staging-user-switcher";
const EXPANDED_STATE_KEY = "userSwitcher.isExpanded";

function getInitialExpandedState(): boolean {
  try {
    const stored = localStorage.getItem(EXPANDED_STATE_KEY);
    return stored ? JSON.parse(stored) : false;
  } catch {
    return false;
  }
}

const initialState: UserSwitcherState = {
  currentUserId: null,
  isExpanded: getInitialExpandedState(),
  activePermissions: [],
  permissionsModified: false,
  originalPermissions: [],
};

export const UserSwitcherContext = createContext<UserSwitcherContextType>({
  ...initialState,
  switchUser: () => {},
  toggleExpanded: () => {},
  togglePermission: () => {},
  resetPermissions: () => {},
  availableCredentials: [],
  isVisible: false,
  getCredentials: () => undefined,
});

function isSwitcherEnabled(): boolean {
  const hostname = window.location.hostname;
  return (
    hostname === STAGING_HOST ||
    hostname.startsWith("stagingv3.") ||
    hostname.includes("staging") ||
    hostname === "kubernetes.docker.internal" ||
    hostname === "localhost" ||
    hostname === "127.0.0.1" ||
    hostname.startsWith("192.168.") ||
    hostname.startsWith("10.")
  );
}

function getUserPermissions(user: User | null): Permission[] {
  if (!user) return [];
  return user.permissions_list || [];
}

interface UserSwitcherProviderProps {
  children: React.ReactNode;
}

export const UserSwitcherProvider: React.FC<UserSwitcherProviderProps> = ({
  children,
}) => {
  const { storefront_id, slug } = useStoreState();
  const { user, updatePermissions, permissions } = useAuthContext();

  const config = stagingUsersConfig as StagingUsersConfig;
  const [state, setState] = useState<UserSwitcherState>(initialState);

  const availableCredentials = useMemo(() => {
    if (!config || !slug) return [];
    const storefrontConfig = config.storefronts[slug];
    return storefrontConfig?.users || [];
  }, [config, slug]);

  const getCredentials = useCallback(
    (userId: string): StagingUserCredentials | undefined => {
      return availableCredentials.find((u) => u.id === userId);
    },
    [availableCredentials]
  );

  const isVisible = useMemo(() => {
    const enabled = isSwitcherEnabled();
    const hasStorefront = storefront_id !== "";
    const hasStorefrontConfig = slug
      ? config.storefronts[slug] !== undefined
      : false;
    console.log("[UserSwitcher] Visibility check:", {
      enabled,
      hasStorefront,
      hasStorefrontConfig,
      slug,
    });
    return enabled && hasStorefront && hasStorefrontConfig;
  }, [config, storefront_id, slug]);

  useEffect(() => {
    if (user && !state.permissionsModified) {
      const userPerms = getUserPermissions(user);
      setState((prev) => ({
        ...prev,
        originalPermissions: userPerms,
        activePermissions: permissions.length > 0 ? permissions : userPerms,
      }));
    }
  }, [user, permissions, state.permissionsModified]);

  useEffect(() => {
    console.log("[UserSwitcher] Storefront changed:", { slug, storefront_id });
    if (!slug || !isSwitcherEnabled()) {
      console.log(
        "[UserSwitcher] Skipping - slug missing or switcher not enabled"
      );
      return;
    }

    const storefrontConfig = config.storefronts[slug];
    if (!storefrontConfig) {
      console.log("[UserSwitcher] No config found for storefront:", slug);
      return;
    }

    console.log("[UserSwitcher] Config loaded successfully for:", slug);

    const stored = sessionStorage.getItem(STORAGE_KEY);
    let selectedUserId: string | undefined;

    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        selectedUserId = parsed.userId;
      } catch {
        // Ignore parse errors
      }
    }

    if (!selectedUserId) {
      selectedUserId = storefrontConfig.default_user_id;
    }

    if (selectedUserId) {
      setState((prev) => ({
        ...prev,
        currentUserId: selectedUserId ?? null,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, storefront_id]);

  const switchUser = useCallback(
    async (userId: string) => {
      const credentials = getCredentials(userId);
      if (!credentials || !storefront_id) {
        console.error("[UserSwitcher] No credentials found for user:", userId);
        return;
      }

      sessionStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ userId: credentials.id })
      );

      setState((prev) => ({
        ...prev,
        currentUserId: credentials.id,
        permissionsModified: false,
        isExpanded: false,
      }));

      console.log(
        "[UserSwitcher] Attempting auto-login as:",
        credentials.email
      );
      try {
        await Axios.post(
          endpoints.v1_storefronts_id_users_login(storefront_id),
          {
            username: credentials.email,
            password: credentials.password,
          }
        );
        window.location.reload();
      } catch (error) {
        console.error(
          "[UserSwitcher] Failed to auto-login as user:",
          credentials.email,
          error
        );
      }
    },
    [getCredentials, storefront_id]
  );

  const toggleExpanded = useCallback(() => {
    setState((prev) => {
      const newExpanded = !prev.isExpanded;
      localStorage.setItem(EXPANDED_STATE_KEY, JSON.stringify(newExpanded));
      return { ...prev, isExpanded: newExpanded };
    });
  }, []);

  const togglePermission = useCallback((permission: Permission) => {
    setState((prev) => {
      const newPermissions = prev.activePermissions.includes(permission)
        ? prev.activePermissions.filter((p) => p !== permission)
        : [...prev.activePermissions, permission];

      return {
        ...prev,
        activePermissions: newPermissions,
        permissionsModified: true,
      };
    });
  }, []);

  const resetPermissions = useCallback(() => {
    setState((prev) => ({
      ...prev,
      activePermissions: prev.originalPermissions,
      permissionsModified: false,
    }));
  }, []);

  useEffect(() => {
    if (state.permissionsModified) {
      updatePermissions(state.activePermissions);
    }
  }, [state.activePermissions, state.permissionsModified, updatePermissions]);

  const value: UserSwitcherContextType = {
    ...state,
    switchUser,
    toggleExpanded,
    togglePermission,
    resetPermissions,
    availableCredentials,
    isVisible,
    getCredentials,
  };

  return (
    <UserSwitcherContext.Provider value={value}>
      {children}
    </UserSwitcherContext.Provider>
  );
};

export const useUserSwitcher = () => useContext(UserSwitcherContext);
